import { Modal, Row, Col } from "antd";
import { useForm } from "react-hook-form";
import { Box, Flex, Checkbox, Text } from "@chakra-ui/react";
import Field from "ui/components/Input/Field";
import PrimaryButton from "ui/components/Button/PrimaryButton";
import { useEffect } from "react";
import { CloseModalButton } from "components/Button/CloseModalButton";
import InputMoney from "components/Input/InputMoney/index";
import { formatMoney } from "infra/helpers/number";
import SelectMedida from "components/Input/SelectMedida/index";
import { OrcamentoCotacaoItem } from "features/orcamento/typings";
import SelectItensCotados from "components/Input/SelectItensCotados/index";
import colors from "core/resources/theme/colors";

interface ModalAdicionarItemProps {
  isOpenModal: boolean;
  onClose: () => any;
  onSaved: (item: any) => any;
  orcamentoId?: string;
}

const defaultValues: OrcamentoCotacaoItem = {
  orcamentoItemId: undefined,
  quantidade: undefined,
  unidadeMedidaId: undefined,
  valorUnitario: undefined,
};

function ModalAdicionarItem({
  isOpenModal,
  onClose,
  onSaved,
  orcamentoId,
}: ModalAdicionarItemProps) {
  const { control, reset, handleSubmit, watch, setValue } =
    useForm<OrcamentoCotacaoItem>({
      defaultValues,
    });

  useEffect(() => {
    if (!isOpenModal) {
      reset(defaultValues);
    }
  }, [isOpenModal, reset]);

  const form = watch();

  const total = (form.quantidade ?? 0) * (form.valorUnitario ?? 0);

  return (
    <Modal
      title="Adicionar item a cotar"
      centered
      visible={isOpenModal}
      footer={
        <Flex justifyContent="end">
          <CloseModalButton close={onClose} closeModalLabel="Cancelar" />
          <PrimaryButton ml={5} mr={2} onClick={handleSubmit(onSaved)}>
            Adicionar
          </PrimaryButton>
        </Flex>
      }
      onCancel={onClose}
      destroyOnClose
      width="900px"
    >
      <Box as={Row} gutter={16} mb={6}>
        <Box as={Col} xs={6}>
          <Field
            control={control}
            name="orcamentoItemId"
            label="Item"
            rules={{ required: true }}
            render={({ field }) => (
              <SelectItensCotados
                orcamentoId={orcamentoId!}
                value={field.value}
                onChange={(value, option: any) => {
                  field.onChange(value);
                  if (option) {
                    setValue("quantidade", option.quantidade);
                    setValue("unidadeMedidaId", option.unidadeMedidaId);
                  }
                }}
              />
            )}
          />
        </Box>
        <Box as={Col} xs={6}>
          <Field
            control={control}
            name="unidadeMedidaId"
            label="Medida"
            rules={{ required: true }}
            render={({ field }) => <SelectMedida {...field} />}
          />
        </Box>
        <Box as={Col} xs={4}>
          <Field
            control={control}
            name="quantidade"
            label="Quantidade"
            rules={{ required: true }}
            render={({ field, isInvalid }) => (
              <InputMoney
                isInvalid={isInvalid}
                onChange={field.onChange}
                value={field.value}
                minValue={0}
              />
            )}
          />
        </Box>
        <Box as={Col} xs={4}>
          <Field
            control={control}
            name="valorUnitario"
            label="Valor Unitário"
            render={({ field, isInvalid }) => (
              <InputMoney
                disabled={form.indisponivel}
                isInvalid={isInvalid}
                onChange={field.onChange}
                value={field.value}
                minValue={0}
              />
            )}
          />
          <Field
            control={control}
            name="indisponivel"
            render={({ field }) => (
              <Checkbox
                mt={2}
                onChange={() => {
                  if (!field.value) {
                    setValue("valorUnitario", undefined);
                  }

                  field.onChange(!field.value);
                }}
                isChecked={!!field.value}
              >
                <Text fontSize={12}>Não tem</Text>
              </Checkbox>
            )}
          />
        </Box>
        <Box as={Col} xs={4}>
          <Flex
            mt="30px"
            justifyContent="flex-end"
            color={form.indisponivel ? colors.red : undefined}
          >
            {formatMoney(total)}
          </Flex>
        </Box>
      </Box>
    </Modal>
  );
}

export default ModalAdicionarItem;
